/**
 * Shared field mapping utilities for switchboard converters
 * Provides reusable functions for field name conversion and field value extraction
 */
import {
  FIELD_NAME_MAPPINGS,
  REVERSE_FIELD_NAME_MAPPINGS,
} from './constants/index'

/**
 * Get field value from HTML object, checking multiple field name variants
 * @param html - HTML object containing legacy fields
 * @param fieldNames - Array of field names to check (in order of priority)
 * @returns The first found field value, or undefined
 */
export function getFieldValueFromHtml(
  html: Record<string, unknown>,
  fieldNames: string[],
): unknown {
  for (const fieldName of fieldNames) {
    if (
      fieldName in html &&
      html[fieldName] !== undefined &&
      html[fieldName] !== null
    ) {
      return html[fieldName]
    }
  }
  return undefined
}

/**
 * Get string field value from HTML object, checking multiple field name variants
 * @param html - HTML object containing legacy fields
 * @param fieldNames - Array of field names to check (in order of priority)
 * @returns The first found field value as string, or undefined
 */
export function getStringFieldValue(
  html: Record<string, unknown>,
  fieldNames: string[],
): string | undefined {
  const value = getFieldValueFromHtml(html, fieldNames)
  return value !== undefined && value !== null ? String(value) : undefined
}

/**
 * Get number field value from HTML object, checking multiple field name variants
 * @param html - HTML object containing legacy fields
 * @param fieldNames - Array of field names to check (in order of priority)
 * @returns The first found field value as number, or undefined
 */
export function getNumberFieldValue(
  html: Record<string, unknown>,
  fieldNames: string[],
): number | undefined {
  const value = getFieldValueFromHtml(html, fieldNames)
  if (value === undefined || value === null) {
    return undefined
  }
  if (typeof value === 'number') {
    return isNaN(value) ? undefined : value
  }
  if (typeof value === 'string') {
    const num = parseFloat(value)
    return isNaN(num) ? undefined : num
  }
  return undefined
}

/**
 * Get boolean field value from HTML object, checking multiple field name variants
 * @param html - HTML object containing legacy fields
 * @param fieldNames - Array of field names to check (in order of priority)
 * @returns The first found field value as boolean, or undefined
 */
export function getBooleanFieldValue(
  html: Record<string, unknown>,
  fieldNames: string[],
): boolean | undefined {
  const value = getFieldValueFromHtml(html, fieldNames)
  if (value === undefined || value === null) {
    return undefined
  }
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    return value === '1' || value === 'true'
  }
  if (typeof value === 'number') {
    return value === 1
  }
  return undefined
}

/**
 * Map a field from HTML to nodeData, checking both snake_case and camelCase variants
 * @param html - HTML object containing legacy fields
 * @param nodeData - Node data object to update
 * @param snakeKey - Snake case field name (e.g., 'on_invalid')
 * @param camelKey - Camel case field name (e.g., 'onInvalid')
 * @param convertNull - If true, convert string "null" to undefined
 */
export function mapField(
  html: Record<string, unknown>,
  nodeData: Record<string, unknown>,
  snakeKey: string,
  camelKey: string,
  convertNull = false,
): void {
  const value = getFieldValueFromHtml(html, [snakeKey, camelKey])
  if (value !== undefined && value !== null) {
    if (convertNull && typeof value === 'string' && value === 'null') {
      nodeData[camelKey] = undefined
    } else {
      nodeData[camelKey] = value
    }
  }
}

/**
 * Map a field from HTML to nodeData with null conversion
 * Converts string "null" and "undefined" to actual undefined
 * @param html - HTML object containing legacy fields
 * @param nodeData - Node data object to update
 * @param snakeKey - Snake case field name
 * @param camelKey - Camel case field name
 */
export function mapFieldWithNullConversion(
  html: Record<string, unknown>,
  nodeData: Record<string, unknown>,
  snakeKey: string,
  camelKey: string,
): void {
  const value = getFieldValueFromHtml(html, [snakeKey, camelKey])
  if (value === undefined || value === null) {
    return
  }
  const strValue = String(value)
  if (strValue === 'null' || strValue === 'undefined') {
    nodeData[camelKey] = undefined
  } else {
    nodeData[camelKey] = value
  }
}

/**
 * Map camelCase keys to snake_case keys for legacy compatibility
 * @param key - Camel case key to convert
 * @returns Snake case key
 */
export function convertKeyToSnakeCase(key: string): string {
  // Direct mappings for specific keys
  if (FIELD_NAME_MAPPINGS[key]) {
    return FIELD_NAME_MAPPINGS[key]
  }

  // Convert camelCase to snake_case for other keys
  // This handles keys like maxWaitTime -> max_wait_time
  return key.replace(/([A-Z])/g, '_$1').toLowerCase()
}

/**
 * Convert snake_case keys to camelCase keys for React Flow format
 * @param key - Snake case key to convert
 * @returns Camel case key
 */
export function convertKeyToCamelCase(key: string): string {
  // Direct reverse mappings for specific keys
  if (REVERSE_FIELD_NAME_MAPPINGS[key]) {
    return REVERSE_FIELD_NAME_MAPPINGS[key]
  }

  // Convert snake_case to camelCase
  return key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * List of Admin3-only metadata fields that should be skipped during conversion
 * These fields are not stored in legacy format
 */
export const ADMIN3_ONLY_FIELDS = [
  'label',
  'mode',
  'toggleCode',
  'status',
  'allowExtensionAsOption',
  'options', // options array should be removed after converting to children
  // Schedule node Admin3-only fields (not in legacy format)
  'holidays',
  'onBusinessHours',
  'onAfterHours',
  // Queue node Admin3-only fields (not in legacy format)
  'queueMusic',
  'queue_music',
] as const

/**
 * List of connection metadata fields that should be skipped during conversion
 * Connections are only stored in endpoints.sources[].target_uuid, not in HTML
 */
export const CONNECTION_METADATA_FIELDS = [
  'onTimeout',
  'on_timeout',
  'whenOpen',
  'when_open',
  'whenClosed',
  'when_closed',
  'onInvalid',
  'on_invalid',
  'onEscape',
  'on_escape',
  'holiday',
  'noAnswer',
  'no_answer',
  'busy',
] as const

/**
 * Check if a field should be skipped during conversion
 * @param key - Field key to check
 * @returns True if the field should be skipped
 */
export function shouldSkipField(key: string): boolean {
  // Skip Admin3-only fields
  if (ADMIN3_ONLY_FIELDS.includes(key as (typeof ADMIN3_ONLY_FIELDS)[number])) {
    return true
  }

  // Skip connection metadata fields
  if (
    CONNECTION_METADATA_FIELDS.includes(
      key as (typeof CONNECTION_METADATA_FIELDS)[number],
    )
  ) {
    return true
  }

  return false
}

/**
 * Convert boolean to legacy number format (0 or 1)
 * Used for: minimized, press_1, auto_reply
 * @param value - Boolean value to convert
 * @returns Number 0 or 1
 */
export function booleanToLegacyNumber(value: boolean): number {
  return value ? 1 : 0
}

/**
 * Convert boolean to legacy string format ("1" or "0")
 * Used for: minimized (change_caller_id, sms), auto_reply (sms)
 * @param value - Boolean value to convert
 * @returns String "1" or "0"
 */
export function booleanToLegacyString(value: boolean): string {
  return value ? '1' : '0'
}

/**
 * Convert legacy string/number boolean to boolean
 * Handles "1"/"0", 1/0, "true"/"false", true/false
 * @param value - Value to convert
 * @returns Boolean value
 */
export function legacyToBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    return value === '1' || value === 'true'
  }
  if (typeof value === 'number') {
    return value === 1
  }
  return false
}

/**
 * Convert null/undefined to legacy string format
 * Legacy sometimes uses string "null" or "undefined" instead of JSON null
 * @param value - Value to convert
 * @param fieldName - Field name to determine conversion pattern
 * @returns String "null", "undefined", or the original value
 */
export function convertNullToLegacyString(
  value: unknown,
  fieldName: string,
): unknown {
  // If value is null or undefined, convert to string based on field pattern
  if (value === null || value === undefined) {
    // Fields that use "undefined" string
    // NOTE: lookup_id should use JSON null, not string "undefined" (handled in baseConverter.ts)
    // Legacy code (line 7652) saves null when no radio button is checked for dial_name
    if (fieldName === 'new_caller_id_option') {
      return 'undefined'
    }
    // Fields that use "null" string (not JSON null)
    // Note: Only leave_voicemail.sound_clip_id uses string "null" (handled in htmlFieldHelpers.ts)
    // extension_id and queue_id use JSON null in legacy (not string "null")
    // Most other fields also use JSON null
    // sound_clip_id is handled specially in htmlFieldHelpers.ts for leave_voicemail
    // For other nodes (auto_attendant, sound_clip), sound_clip_id should be JSON null
    // but that's handled in node-specific converters, so we default to JSON null here
    // Most other fields use JSON null
    return null
  }
  return value
}
